import { FormattedMessage } from '../../../../util/reactIntl';
import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { LinkedLogo, NamedLink } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={css.hero}>
          <IconCard type="logo" />
          <Field data={slogan} className={css.headline} />

          {/* <a href="/p/photographer-home" type="button" className={css.cta}>
            Apply in 3 min
          </a> */}
        </div>

        <ul className={css.navLinks}>
          <li>
            <a href="/p/about"><FormattedMessage id="TopbarDesktop.aboutUs" /></a>
          </li>
          <li>
            <a href="/p/contact-us"><FormattedMessage id="TopbarDesktop.contactUs" /></a>
          </li>
          <li>
            <a href="/p/faq"><FormattedMessage id="TopbarDesktop.faqs" /></a>
          </li>
          <li>
            <a href="/p/photographer-home"><FormattedMessage id="SectionFooter.photographers" /></a>
          </li>
          <li>
            <a href="/p/privacy-policy"><FormattedMessage id="SectionFooter.privacyPolicy" /></a>
          </li>
          <li>
            <a href="/p/terms-of-service"><FormattedMessage id="SectionFooter.termsOfUse" /></a>
          </li>
        </ul>

        <div className={css.bottomBar}>
          <div className={css.copyText}>
            {copyright ? (
              <Field data={copyright} />
            ) : (
              <span>
                <FormattedMessage
                  id="SectionFooter.copyright"
                  values={{ year: new Date().getFullYear() }}
                />
              </span>
            )}
          </div>
          <div className={css.social}>
            <span className={css.socialLabel}><FormattedMessage id="SectionFooter.beSocial" /></span>
            <a href="#">
              <IconCard type="facebook" />
            </a>
            <a href="#">
              <IconCard type="instagram" />
            </a>
            <a href="#">
              <IconCard type="linkedin" />
            </a>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
